import {
  DiscoveryApi,
  FetchApi,
} from '@backstage/core-plugin-api';

import {
  ApiMessageResponse,
  CreateExternalUserPayload,
  ExternalUsersApi,
  ExternalUsersListParams,
  UpdateExternalUserPayload,
} from './ExternalUsersApi';

export class ExternalUsersClient
  implements ExternalUsersApi {
  constructor(
    private readonly discoveryApi: DiscoveryApi,

    private readonly fetchApi: FetchApi,
  ) { }

  // =====================================
  // BASE URL
  // =====================================

  private async getBaseUrl() {
    return await this.discoveryApi.getBaseUrl(
      'external-onboarding',
    );
  }

  // =====================================
  // LIST
  // =====================================

  async list(params?: ExternalUsersListParams) {
    const baseUrl = await this.getBaseUrl();

    const query = params
      ? new URLSearchParams(
        Object.entries(params)
          .filter(([, v]) => v !== undefined && v !== null)
          .map(([k, v]) => [k, String(v)]),
      ).toString()
      : '';

    const url = query
      ? `${baseUrl}/external-users?${query}`
      : `${baseUrl}/external-users`;

    const response = await this.fetchApi.fetch(url);

    if (!response.ok) {
      throw new Error('Error loading external users');
    }

    const data = await response.json();

    return Array.isArray(data) ? data : [data];
  }

  // =====================================
  // GET BY ID
  // =====================================

  async getById(
    objectid: string,
  ) {
    const baseUrl =
      await this.getBaseUrl();

    const response =
      await this.fetchApi.fetch(
        `${baseUrl}/external-users/${objectid}`,
      );

    if (!response.ok) {
      throw new Error(
        'Error loading external user',
      );
    }

    return response.json();
  }

  async getByMail(mail: string) {
    const baseUrl = await this.getBaseUrl();

    const response = await this.fetchApi.fetch(
      `${baseUrl}/external-users?mail=${encodeURIComponent(mail)}`,
    );

    if (response.status === 404) {
      return null;
    }

    if (!response.ok) {
      throw new Error('Error fetching user');
    }

    const data = await response.json();

    if (Array.isArray(data)) {
      return data.length > 0 ? data[0] : null;
    }

    return data ?? null;
  }

  // =====================================
  // CREATE
  // =====================================

  async create(
    payload: CreateExternalUserPayload,
  ): Promise<ApiMessageResponse> {
    const baseUrl =
      await this.getBaseUrl();

    const response =
      await this.fetchApi.fetch(
        `${baseUrl}/external-users`,
        {
          method: 'POST',

          headers: {
            'Content-Type':
              'application/json',
          },

          body: JSON.stringify(
            payload,
          ),
        },
      );

    if (!response.ok) {
      throw new Error(
        'Error creating external user',
      );
    }

    return response.json();
  }

  // =====================================
  // UPDATE
  // =====================================

  async update(
    objectid: string,

    payload: UpdateExternalUserPayload,
  ): Promise<ApiMessageResponse> {
    const baseUrl =
      await this.getBaseUrl();

    const response =
      await this.fetchApi.fetch(
        `${baseUrl}/external-users/${objectid}`,
        {
          method: 'PATCH',

          headers: {
            'Content-Type':
              'application/json',
          },

          body: JSON.stringify(
            payload,
          ),
        },
      );

    if (!response.ok) {
      throw new Error(
        'Error updating external user',
      );
    }

    return response.json();
  }

  // =====================================
  // DELETE
  // =====================================

  async delete(
    objectid: string,
  ): Promise<ApiMessageResponse> {
    const baseUrl =
      await this.getBaseUrl();

    const response =
      await this.fetchApi.fetch(
        `${baseUrl}/external-users/${objectid}`,
        {
          method: 'DELETE',

          headers: {
            'Content-Type':
              'application/json',
          },
        },
      );

    if (!response.ok) {
      throw new Error(
        'Error deleting external user',
      );
    }

    return response.json();
  }
}